import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { resetPasswordUser } from "../redux/features/auth/authSlice";

function ResetPassword() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const { resetPasswordLoading } = useSelector((state) => state.auth);
  const [errors, setErrors] = useState({});
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

  const tokenFromUrl = searchParams.get("token") || "";
  const emailFromUrl = searchParams.get("email") || "";

  const [form, setForm] = useState({
    email: emailFromUrl,
    token: tokenFromUrl,
    password: "",
    confirmPassword: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const newErrors = {};

    if (!form.password) {
      newErrors.password = "New password is required";
    } else if (form.password.length < 6) {
      newErrors.password = "Password must be at least 6 characters long";
    }

    if (!form.confirmPassword) {
      newErrors.confirmPassword = "Confirm password is required";
    } else if (form.password !== form.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) return;

    try {
      await dispatch(
        resetPasswordUser({
          token: form.token,
          data: {
            email: form.email,
            token: form.token,
            password: form.password,
            password_confirmation: form.confirmPassword,
          },
        }),
      ).unwrap();

      toast.success("Password Reset successfully!");
      navigate("/login");
    } catch (error) {
      toast.error(error.message || "Failed to Reset Password. Try again.");
    }
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center bg-slate-100 p-4">
      <div className="bg-white rounded-2xl border-4 border-white shadow-sm flex justify-center w-full max-w-5xl min-h-[32rem] overflow-hidden">
        <div className=" w-full lg:w-3/4 mx-auto flex flex-col justify-center text-center p-6 md:p-8">
          <h1 className="font-bold text-3xl mb-2">Reset Password</h1>
          <p className="text-gray-600 text-base mb-6 capitalize">
            Please enter your new password below.
          </p>

          <form onSubmit={handleSubmit}>
            <div className="flex flex-col space-y-6">
              {/* New Password */}
              <div className="flex flex-col items-start gap-1 w-full">
                <label className="text-lg font-semibold text-gray-700">
                  New Password
                </label>
                <div className="relative w-full">
                  <input
                    name="password"
                    type={passwordVisible ? "text" : "password"}
                    className="border border-slate-200 rounded p-2 w-full focus:outline-none focus:ring-2 focus:ring-lightPurple"
                    placeholder="Enter New Password"
                    value={form.password}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    onClick={() => setPasswordVisible(!passwordVisible)}
                    className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
                  >
                    <FontAwesomeIcon
                      icon={passwordVisible ? faEye : faEyeSlash}
                    />
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-sm font-medium pt-1 pl-1">
                    {errors.password}
                  </p>
                )}
              </div>

              {/* Confirm Password */}
              <div className="flex flex-col items-start gap-1 w-full">
                <label className="text-lg font-semibold text-gray-700">
                  Confirm Password
                </label>
                <div className="relative w-full">
                  <input
                    name="confirmPassword"
                    type={confirmPasswordVisible ? "text" : "password"}
                    className="border border-slate-200 rounded p-2 w-full focus:outline-none focus:ring-2 focus:ring-lightPurple"
                    placeholder="Confirm New Password"
                    value={form.confirmPassword}
                    onChange={handleChange}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setConfirmPasswordVisible(!confirmPasswordVisible)
                    }
                    className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
                  >
                    <FontAwesomeIcon
                      icon={confirmPasswordVisible ? faEye : faEyeSlash}
                    />
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-red-500 text-sm font-medium pt-1 pl-1">
                    {errors.confirmPassword}
                  </p>
                )}
              </div>

              {/* Submit button */}
              <button
                type="submit"
                disabled={resetPasswordLoading}
                className={`bg-lightPurple text-white p-2.5 rounded-lg font-semibold transition duration-300 mt-2 ${
                  resetPasswordLoading
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-purpleColor"
                }`}
              >
                {resetPasswordLoading && (
                  <FontAwesomeIcon
                    icon={faSpinner}
                    className="animate-spin mr-1"
                  />
                )}
                {resetPasswordLoading ? "Resetting..." : "Reset Password"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
